import axios from "axios";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import toast from "react-hot-toast";
import { getSecureUser } from "@/utils/secureStorage";
import { Notification, NotificationContextType } from "../types";

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);

  // Get user id from secure storage (or context if available)
  const userId = (() => {
    try {
      const user = getSecureUser();
      return user?._id || user?.id || null;
    } catch {
      return null;
    }
  })();

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!userId) return;
      try {
        setLoading(true);
        const res = await axios.get(
          `${import.meta.env.VITE_BACKEND_ENDPOINT || "https://api.production.gnzabe.com"}/api/v1/notifications/${userId}`
        );
        const backendNotifications = res.data?.data?.notifications || [];
        setNotifications(
          Array.isArray(backendNotifications) ? backendNotifications : []
        );
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to fetch notifications";
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, [userId]);

  const addNotification = useCallback((notification: Notification) => {
    // Enhance notifications with additional context based on real backend data
    let enhancedNotification = { ...notification };

    // Extract and enhance metadata from backend
    if (notification.metadata) {
      // Normalize common field names from backend
      const normalizedMetadata = {
        ...notification.metadata,
        // Normalize common field variations
        employeeName:
          notification.metadata.employeeName ||
          notification.metadata.employee_name ||
          notification.metadata.userName ||
          notification.metadata.user_name,
        department:
          notification.metadata.department ||
          notification.metadata.departmentName ||
          notification.metadata.deptName,
        location:
          notification.metadata.location ||
          notification.metadata.city ||
          notification.metadata.country,
        device:
          notification.metadata.device ||
          notification.metadata.deviceType ||
          notification.metadata.userAgent,
        ipAddress:
          notification.metadata.ipAddress ||
          notification.metadata.ip ||
          notification.metadata.clientIP,
        courseName:
          notification.metadata.courseName ||
          notification.metadata.course_name ||
          notification.metadata.courseTitle ||
          notification.metadata.title,
        score:
          notification.metadata.score ||
          notification.metadata.grade ||
          notification.metadata.percentage,
        completionDate:
          notification.metadata.completionDate ||
          notification.metadata.completedAt ||
          notification.metadata.finishedAt,
        previousStatus:
          notification.metadata.previousStatus ||
          notification.metadata.oldStatus ||
          notification.metadata.fromStatus,
        newStatus:
          notification.metadata.newStatus ||
          notification.metadata.currentStatus ||
          notification.metadata.toStatus,
        updatedBy:
          notification.metadata.updatedBy ||
          notification.metadata.changedBy ||
          notification.metadata.modifiedBy,
        updateType:
          notification.metadata.updateType ||
          notification.metadata.type ||
          notification.metadata.category,
        priority:
          notification.metadata.priority ||
          notification.metadata.severity ||
          notification.metadata.level,
        impact:
          notification.metadata.impact ||
          notification.metadata.effect ||
          notification.metadata.consequence,
        scheduledTime:
          notification.metadata.scheduledTime ||
          notification.metadata.scheduledAt ||
          notification.metadata.plannedTime,
      };

      enhancedNotification.metadata = normalizedMetadata;
    }

    // Add type-specific enhancements
    if (
      notification.title?.toLowerCase().includes("course assignment request") ||
      notification.message?.toLowerCase().includes("course assignment request")
    ) {
      enhancedNotification = {
        ...enhancedNotification,
        metadata: {
          ...enhancedNotification.metadata,
          notificationType: "course_assignment_request",
          requiresAction: true,
          priority: "medium",
        },
      };
    }

    const newNotification = {
      ...enhancedNotification,
      id:
        enhancedNotification.id ||
        enhancedNotification._id ||
        Date.now().toString(),
      read: false,
      createdAt: enhancedNotification.createdAt || new Date(),
    };

    setNotifications((prev) => {
      prev = Array.isArray(prev) ? prev : [];
      // Filter out duplicates by id or _id
      const exists = prev.some(
        (n) =>
          n.id === newNotification.id ||
          n._id === newNotification.id ||
          n.id === newNotification._id ||
          n._id === newNotification._id
      );
      if (exists) return prev;
      return [newNotification, ...prev];
    });
  }, []);

  // Mark a single notification as read (calls backend)
  const markAsRead = useCallback(async (notificationId: string) => {
    setLoading(true);
    try {
      await axios.post(
        `${import.meta.env.VITE_BACKEND_ENDPOINT || "https://api.production.gnzabe.com"}/api/v1/notifications/mark-notification-as-read/${notificationId}`
      );
      setNotifications((prev) => {
        prev = Array.isArray(prev) ? prev : [];
        return prev.map((notif) =>
          notif.id === notificationId || notif._id === notificationId
            ? { ...notif, read: true }
            : notif
        );
      });
      toast.success("Notification marked as read");
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to mark notification as read";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark all notifications as read (calls backend)
  const markAllAsRead = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      await axios.post(
        `${import.meta.env.VITE_BACKEND_ENDPOINT || "https://api.production.gnzabe.com"}/api/v1/notifications/mark-all-notifications-as-read/${userId}`
      );
      setNotifications((prev) => {
        prev = Array.isArray(prev) ? prev : [];
        return prev.map((notif) => ({ ...notif, read: true }));
      });
      toast.success("All notifications marked as read");
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to mark all notifications as read";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Mark a single notification as read and inactive (calls backend)
  const removeNotification = useCallback(
    async (notificationId: string) => {
      if (!userId) return;
      setLoading(true);
      try {
        await axios.post(
          `${import.meta.env.VITE_BACKEND_ENDPOINT || "https://api.production.gnzabe.com"}/api/v1/notifications/clear-notification/${notificationId}`
        );
        // Update local state to mark as read and inactive
        setNotifications((prev) => {
          prev = Array.isArray(prev) ? prev : [];
          return prev.map((notif) =>
            notif.id === notificationId || notif._id === notificationId
              ? { ...notif, read: true, isActive: false }
              : notif
          );
        });
        toast.success("Notification marked as read");
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to mark notification as read";
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  // Mark all notifications as read and inactive (calls backend)
  const clearAllNotifications = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      await axios.post(
        `${import.meta.env.VITE_BACKEND_ENDPOINT || "https://api.production.gnzabe.com"}/api/v1/notifications/clear-all-notifications/${userId}`
      );
      // Update local state to mark all as read and inactive
      setNotifications((prev) => {
        prev = Array.isArray(prev) ? prev : [];
        return prev.map((notif) => ({ ...notif, read: true, isActive: false }));
      });
      toast.success("All notifications marked as read");
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to mark all notifications as read";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const unreadCount = notifications.filter(
    (notif) => !notif.read && notif.isActive !== false
  ).length;

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
